import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Toggle from '../ui/Toggle';
import { LabelWithHelp } from '../ui/FieldHelp';
import { Card } from '../ui/primitives';
import CircularProgress from '@mui/material/CircularProgress';
import { useAppContext } from '../../appprovider';
import {
  canShowInAppPurchaseBillingSetting,
  isOrgInAppPurchaseEnabled,
} from '../services/instructorMeterLimits';
import { updateOrgInAppPurchase } from '../services/billingInAppPurchaseApi';
import { fetchConnectStatus } from '../services/connectApi';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const CardTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 750;
  color: ${({ theme }) => theme.text};
`;

const CardText = styled.p`
  margin: 0 0 12px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.5;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const PlanHint = styled.p`
  margin: 10px 0 0;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.45;
`;

const ErrorNote = styled.p`
  margin: 10px 0 0;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.danger};
  line-height: 1.45;
`;

const HELP = {
  inAppPurchase: 'When on, instructors can enable in-app purchase on individual courses and assessments. Requires Stripe Connect.',
};

/**
 * Billing master switch — gates IAP toggles on course and assessment settings.
 */
export default function BillingInAppPurchaseSetting({ userId }) {
  const { entitlements, loadEntitlements } = useAppContext();
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // In-app purchase collects learner payments through Stripe, so it can only be
  // enabled once the org's payout account is connected and payouts are enabled.
  const [payoutsReady, setPayoutsReady] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!userId) { setStatusLoaded(true); return undefined; }
    fetchConnectStatus(userId)
      .then((s) => { if (alive) setPayoutsReady(!!s.payoutsEnabled); })
      .catch(() => {})
      .finally(() => { if (alive) setStatusLoaded(true); });
    return () => { alive = false; };
  }, [userId]);

  if (!canShowInAppPurchaseBillingSetting(entitlements)) {
    return null;
  }

  const orgId = entitlements?.orgId;
  const enabled = payoutsReady && isOrgInAppPurchaseEnabled(entitlements);
  const canToggle = payoutsReady && !saving;

  const handleToggle = async (checked) => {
    if (!guardAction('action.billing.iapToggle')) return;
    if (!userId || !orgId || saving || !payoutsReady) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateOrgInAppPurchase({ userId, orgId, enabled: checked });
      await loadEntitlements(userId);
    } catch (err) {
      setError('Unable to update in-app purchase setting right now.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Card $pad style={{ marginBottom: 16 }}>
      <CardTitle>In-app purchase</CardTitle>
      <CardText>
        Control whether your organization can charge learners through the app on courses and assessments.
        Learner payments are collected through Stripe and paid to your connected account.
      </CardText>
      <Row>
        <Toggle
          checked={enabled}
          disabled={!canToggle}
          onChange={handleToggle}
          label={<LabelWithHelp help={HELP.inAppPurchase}>Enable in-app purchase</LabelWithHelp>}
        />
        {saving && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#64748b' }}>
            <CircularProgress size={15} thickness={5} />
            Saving…
          </span>
        )}
      </Row>
      {statusLoaded && !payoutsReady ? (
        <PlanHint>
          Connect a payout-ready Stripe account (in the Payouts card above) to enable in-app purchase —
          learners can only be charged once your account can receive payouts.
        </PlanHint>
      ) : null}
      {error ? <ErrorNote>{error}</ErrorNote> : null}
    </Card>
    <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}
